import React from "react";
import { Modal } from "react-bootstrap";
import { useIntl } from "react-intl";
import { useNavigate } from "react-router-dom-v5-compat";
import { useQueryClient } from "@tanstack/react-query";
import { SubmitButton } from "../Form";
import { withNotifier, withConfiguration, useNotifier } from "../../context";
import { capitalizeFirstLetter } from "../../utils";
import { useApi, useSubscriptionQuery, useUpgradeTarget } from "../../hooks";

type Props = {
  asset?: {
    id?: number;
    serial_number?: string;
  };
  configuration: {
    appName?: string;
    baseUrl?: string;
  };
};

export function AssignAssetToSubscriptionModal({
  asset,
  configuration,
}: Props) {
  const api = useApi();
  const intl = useIntl();
  const notifier = useNotifier();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isRedirecting, setIsRedirecting] = React.useState(false);
  const { data: subscription } = useSubscriptionQuery();
  const { target: upgradeTarget, isReady: upgradeReady } = useUpgradeTarget();

  const { appName } = configuration;
  const capitalizedAppName = capitalizeFirstLetter(appName);
  const assetsLeftInSubscription =
    !subscription ||
    subscription.asset_quota === -1 ||
    subscription.asset_quota > subscription.number_assigned_assets;
  const canUpgrade = upgradeReady && !!upgradeTarget;
  const showUpgradeButton = assetsLeftInSubscription || canUpgrade;
  const showCannotUpgradeHint =
    !assetsLeftInSubscription && upgradeReady && !upgradeTarget;

  const { mutate, isLoading } = api.post.useMutation(
    `/subscriptions/${subscription?.id}/assets`,
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["/subscription"] });
        setIsRedirecting(true);
        notifier.showSuccess(
          intl.formatMessage({
            id: "subscription.asset_assigned_success_message",
          }),
        );
        navigate(0);
      },
    },
  );

  const onAccept = async () => {
    if (assetsLeftInSubscription) {
      // @ts-expect-error TS(2345) mutate() signature is over-strict; asset.id is validated by the caller.
      mutate({ assets: [{ id: asset.id }] });
      return;
    }

    // Subscription full: navigate to upgrade
    // upgradeTarget guaranteed non-null by render condition when button is shown
    navigate(`${configuration.baseUrl}${upgradeTarget}`);
  };

  const onDecline = async () => {
    navigate(-1);
  };

  return (
    <Modal
      dialogClassName="asset-subscription-assignment-modal"
      show
      restoreFocus={false}
      // @ts-expect-error TS(2769) FIXME: No overload matches this call.
      size="sm"
      backdrop="static"
    >
      <Modal.Header closeButton={false}>
        <Modal.Title>
          {intl.formatMessage(
            { id: "subscription.asset_assignment_modal.title" },
            { appName: capitalizedAppName },
          )}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="center">
          <div className="apps-menu">
            <div className="menu-item">
              <div className="menu-app-icon">
                <span
                  // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
                  className={`icon lcm-iot-icon-${appName.toLowerCase()}`}
                />
              </div>
              <span className="title">
                <span>{capitalizedAppName}</span>
              </span>
            </div>
          </div>
        </div>
        <p>
          {intl.formatMessage(
            {
              id: !assetsLeftInSubscription
                ? "subscription.asset_assignment_modal.message.subscription_full"
                : "subscription.asset_assignment_modal.message",
            },
            // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
            { appName: capitalizedAppName, serialNumber: asset.serial_number },
          )}
        </p>
        {showCannotUpgradeHint && (
          <p
            id="asset-subscription-assign-cannot-upgrade-hint"
            className="text-muted"
          >
            {intl.formatMessage({
              id: "subscription.asset_assignment_modal.cannot_upgrade_hint",
            })}
          </p>
        )}
      </Modal.Body>
      <Modal.Footer>
        <div className="btn-group justify-middle">
          {showUpgradeButton && (
            <SubmitButton
              fetching={isLoading || isRedirecting}
              id="asset-subscription-assign-accept"
              onClick={onAccept}
              text={intl.formatMessage({
                id: !assetsLeftInSubscription
                  ? "subscription.asset_assignment_modal.upgrade"
                  : "subscription.asset_assignment_modal.accept",
              })}
              disabled={isLoading || isRedirecting}
            />
          )}
          <button
            id="asset-subscription-assign-decline"
            type="button"
            className="btn btn-default"
            onClick={onDecline}
            disabled={isLoading || isRedirecting}
          >
            {intl.formatMessage({
              id: "subscription.asset_assignment_modal.cancel",
            })}
          </button>
        </div>
      </Modal.Footer>
    </Modal>
  );
}

// @ts-expect-error TS(2345) FIXME: Argument of type '({ asset, configuration, }: Prop... Remove this comment to see the full error message
export default withConfiguration(withNotifier(AssignAssetToSubscriptionModal));
